<template>
  <base-layout :withHeaderStructure="true">
    <template v-slot:header>
      <nav v-if='isDetailPage'>
        <router-link to="/news" class="flex items-center text-sm leading-5 font-medium text-gray-400 hover:text-gray-200 focus:outline-none focus:underline transition duration-150 ease-in-out">
          <svg class="flex-shrink-0 -ml-1 mr-1 h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          Back to news
        </router-link>
      </nav>
      <div v-if='isDetailPage'>
        <h1 class="font-bold text-gray-900 text-xl sm:text-2xl sm:truncate tracking-tight leading-6 sm:leading-9">
          {{newsPostEnriched.title}}
        </h1>
        <h2  class="text-gray-900 text-lg leading-9 sm:truncate tracking-tight">News
          <span v-if="categoryTitle && !isDetailPage"> - {{categoryTitle}}</span>
          <span v-if="isDetailPage && newsPostEnriched.catId"> - {{getCategoryNameByIdLong(newsPostEnriched.catId)}}</span>
        </h2> 
      </div>
      <div v-else>
        <h2 class="font-bold text-2xl">News
          <span v-if="categoryTitle" > - {{ categoryTitle }}</span> 
        </h2>
        <h3 v-if="categoryDescription" class="text-sm">{{ categoryDescription }}</h3>
      </div>
    </template>
    <template v-slot:main>
      <div>
        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-12 lg:col-span-9 text-sm mt-4 md:mt-0">
            <!-- Skeleton loader when content is loading -->
            <div v-if='isFetchingNews' class='w-full'>
              <!-- First news item skeleton -->
              <div class='bg-white shadow overflow-hidden sm:rounded-lg py-8 px-6 mb-8'>
                <div class='float-right w-10 h-10 bg-gray-300 rounded-full animate-pulse'></div>
                
                <div class='h-7 bg-gray-300 rounded w-3/4 mb-4 animate-pulse'></div>
                <div class='h-4 bg-gray-300 rounded w-1/3 mb-8 animate-pulse'></div>
                <div class='h-64 bg-gray-300 rounded animate-pulse'></div>
                
                <div class='space-y-3 mt-8'>
                  <div class='h-4 bg-gray-300 rounded animate-pulse'></div>
                  <div class='h-4 bg-gray-300 rounded w-5/6 animate-pulse'></div>
                  <div class='h-4 bg-gray-300 rounded w-4/6 animate-pulse'></div>
                  <div class='h-4 bg-gray-300 rounded w-3/4 animate-pulse'></div>
                </div>
                
                <div class='h-4 bg-gray-300 rounded w-1/6 mt-4 animate-pulse'></div>
                
                <div class='hidden md:grid grid-cols-2 w-full border-t mt-4 pt-4'>
                  <div class='h-4 bg-gray-300 rounded w-1/3 animate-pulse'></div>
                  <div class='grid grid-cols-3 gap-2 ml-auto mr-0 w-1/4'>
                    <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                    <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                    <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                  </div>
                </div>
              </div>
              
              <!-- Grid of news items skeleton -->
              <div class='w-full grid grid-cols-1 md:grid-cols-2 md:gap-6'>
                <div v-for="index in 4" :key="index" class='flex flex-1 flex-col justify-between bg-white shadow overflow-hidden sm:rounded-lg py-8 px-6 mb-2'>                   
                  <div>
                    <div class='h-7 bg-gray-300 rounded w-4/5 mb-4 animate-pulse'></div>
                    <div class='h-4 bg-gray-300 rounded w-1/3 mb-8 animate-pulse'></div>
                    
                    <div class='h-48 bg-gray-300 rounded animate-pulse mb-3'></div>
                    <div class='space-y-3'>
                      <div class='h-4 bg-gray-300 rounded animate-pulse'></div>
                      <div class='h-4 bg-gray-300 rounded w-5/6 animate-pulse'></div>
                      <div class='h-4 bg-gray-300 rounded w-3/4 animate-pulse'></div>
                    </div>
                  </div>
                  
                  <div class='mt-auto mb-0'>
                    <div class='h-4 bg-gray-300 rounded w-1/4 mt-4 animate-pulse'></div>
                  </div>
                  
                  <div class='hidden md:grid grid-cols-2 w-full border-t mt-4 pt-4'>
                    <div class='h-4 bg-gray-300 rounded w-1/2 animate-pulse'></div>
                    <div class='grid grid-cols-3 gap-2 ml-auto mr-0 w-1/2'>
                      <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                      <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                      <div class='h-7 w-7 bg-gray-300 rounded animate-pulse'></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div v-if='!isDetailPage'>
                <div v-if="!firstNewsItem || !firstNewsItem.id">
                  <div class="flex flex-col items-center justify-center py-16 px-6 text-center bg-white rounded-lg">
                    <div class="flex items-center justify-center w-32 h-32 mb-5 rounded-full bg-gray-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-16 h-16 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="1.5"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l4 4v10a2 2 0 01-2 2z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M13 4v5h5M8 13h8M8 17h5"
                        />
                      </svg>
                    </div>

                    <h3 class="text-lg font-semibold text-gray-700">
                      No news found
                    </h3>

                    <p class="mt-1 text-sm text-gray-500 max-w-sm">
                      There are no news articles available at the moment.
                    </p>
                  </div>
                </div>
                <div v-else>
                  <div class='w-full'>
                    <div class='bg-white shadow overflow-hidden sm:rounded-lg py-8 px-6 mb-8'>
                      <div v-if='firstNewsItem.isPinned==1'>
                        <img class="float-right object-fill h-5" src="/img/pin.png" title="Pinned News Article">
                      </div>
                      <a href='javascript:void(0)' @click="navToNews(firstNewsItem.id, firstNewsItem.cat_url, firstNewsItem.titleSlug)" class="text-lg font-bold leading-5 text-blue-700 hover:text-blue-700 mt-4 mb-2 hover:underline"><h2>{{firstNewsItem.title}}</h2></a>
                      <div class='text-xs text-grey-500'>Posted by {{firstNewsItem.by}} on {{new Date(firstNewsItem.date).toLocaleDateString('en-US', {month: 'long', year: 'numeric', day: 'numeric'})}}</div>
                      
                      <div v-if="isNewsSummary">
                        <div class='h-64 bg-gray-300 rounded animate-pulse mt-6'></div>
                        <div class='space-y-3 mt-10'>
                          <div class='h-4 bg-gray-300 rounded animate-pulse'></div>
                          <div class='h-4 bg-gray-300 rounded w-5/6 animate-pulse'></div>
                          <div class='h-4 bg-gray-300 rounded w-4/6 animate-pulse'></div>
                          <div class='h-4 bg-gray-300 rounded w-3/4 animate-pulse'></div>
                        </div>
                        <div class='h-4 bg-gray-300 rounded w-1/6 mt-4 animate-pulse'></div>
                      </div>


                      <div v-else class="news-body mt-8 leading-5">
                        <!-- Image carousel -->
                        <div v-if="firstNewsItem.images && firstNewsItem.images.length"  class="news-summary-carousel mb-4">
                          <div class="relative w-full overflow-hidden rounded-lg" style="height: 500px;">

                            <transition name="slide" mode="out-in">
                              <img
                                :key="firstNewsItem.id + '-' + (newsImageIndexes[firstNewsItem.id] || 0)"
                                :src="'https://www.thebrokersite.com/images/news/articles/' +
                                    encodeURIComponent(
                                        firstNewsItem.images[newsImageIndexes[firstNewsItem.id] || 0].filename
                                    )"
                                :alt="firstNewsItem.title"
                                class="w-full h-full object-contain"
                              >
                            </transition>

                            <!-- Previous -->
                            <button v-if="firstNewsItem.images.length > 1" @click.stop="prevNewsSummaryImage(firstNewsItem)"
                              class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full w-9 h-9 flex items-center justify-center z-10">
                              &#10094;
                            </button>

                            <!-- Next -->
                            <button v-if="firstNewsItem.images.length > 1" @click.stop="nextNewsSummaryImage(firstNewsItem)"
                              class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full w-9 h-9 flex items-center justify-center z-10">
                              &#10095;
                            </button>

                            <!-- Indicators -->
                            <div v-if="firstNewsItem.images.length > 1"
                              class="absolute bottom-2 left-0 right-0 flex justify-center items-center gap-1 z-10">
                              <button
                                  v-for="(image, imageIndex) in firstNewsItem.images"
                                  :key="imageIndex"
                                  @click.stop="goToNewsSummaryImage(firstNewsItem, imageIndex)"
                                  class="rounded-full transition-all duration-200"
                                  :class="(newsImageIndexes[firstNewsItem.id] || 0) === imageIndex
                                      ? 'w-3 h-3 bg-blue-600'
                                      : 'w-2 h-2 bg-white bg-opacity-70'"
                              >
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- News body -->
                        <div
                            v-html="firstNewsItem.bodyHtml"
                            class="h-98 overflow-hidden text-ellipsis"
                        ></div>
                      </div>
                      <!-- <div v-else v-html="firstNewsItem.bodyHtml" class='news-body mt-8 leading-5'></div> -->

                      <a v-if='firstNewsItem.doReadMore' href='javascript:void(0)' @click="navToNews(firstNewsItem.id, firstNewsItem.cat_url, firstNewsItem.titleSlug)" class='block mt-2 text-blue-700 hover:text-blue-700 hover:underline'>Read More</a>
                      <div class='grid grid-cols-2 w-full border-t mt-4 pt-4'>
                        <div>
                          <router-link :to="`/news/${getCategoryNameById(firstNewsItem.catId)}`" class='text-blue-700 hover:text-blue-700 truncate hover:underline'>More Related News</router-link>
                        </div>
                        <div v-if='settingsMap.enableNewsShareButtons == 1' class='grid grid-cols-3 gap-2 ml-auto mr-0 invisible md:visible'>
                          <div>
                            <a :href="'https://www.facebook.com/sharer/sharer.php?u='+encodeURIComponent('https://news.thebrokersite.com/?id='+firstNewsItem.id+'&share=facebook')" target="_blank" rel="noopener" title="Share on Facebook"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <path d="M 85.033 90 C 87.776 90 90 87.776 90 85.033 V 4.967 C 90 2.223 87.776 0 85.033 0 H 4.967 C 2.223 0 0 2.223 0 4.967 v 80.066 C 0 87.776 2.223 90 4.967 90 H 85.033 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(60,90,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 50.916 83.204 V 48.351 h 11.699 l 1.752 -13.583 h -13.45 v -8.672 c 0 -3.933 1.092 -6.612 6.731 -6.612 l 7.193 -0.003 V 7.332 c -1.244 -0.166 -5.513 -0.535 -10.481 -0.535 c -10.37 0 -17.47 6.33 -17.47 17.954 v 10.017 H 25.16 v 13.583 h 11.729 v 34.853 H 50.916 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>

                          <div>
                            <a :href="'https://www.linkedin.com/shareArticle?mini=true&url='+encodeURIComponent('https://news.thebrokersite.com/?id='+firstNewsItem.id+'&share=linkedin')" target="_blank" rel="noopener" title="Share on LinkedIn"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <path d="M 0 6.447 C 0 2.887 2.978 0 6.651 0 h 76.698 C 87.022 0 90 2.887 90 6.447 v 77.106 C 90 87.114 87.022 90 83.349 90 H 6.651 C 2.978 90 0 87.114 0 83.553 V 6.447 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,102,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 20.485 29.151 c 4.74 0 7.691 -3.121 7.691 -7.021 c -0.088 -3.988 -2.95 -7.022 -7.601 -7.022 c -4.65 0 -7.69 3.034 -7.69 7.022 c 0 3.9 2.95 7.021 7.512 7.021 H 20.485 L 20.485 29.151 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 27.282 75.339 v -40.64 H 13.688 v 40.64 H 27.282 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 34.804 75.339 h 13.594 V 52.644 c 0 -1.215 0.088 -2.428 0.447 -3.296 c 0.983 -2.427 3.219 -4.94 6.975 -4.94 c 4.919 0 6.887 3.727 6.887 9.19 v 21.741 h 13.592 V 52.037 c 0 -12.483 -6.706 -18.291 -15.65 -18.291 c -7.333 0 -10.553 4.073 -12.342 6.847 h 0.091 v -5.894 H 34.804 C 34.982 38.513 34.804 75.339 34.804 75.339 L 34.804 75.339 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>

                          <div>
                            <a :href="'https://twitter.com/intent/tweet?text=' + encodeURIComponent(firstNewsItem.title) + '+' + encodeURIComponent('https://news.thebrokersite.com/?id='+firstNewsItem.id+'&share=x')" target="_blank" rel="noopener" title="Share on X (Twitter)"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <polygon points="24.89,23.01 57.79,66.99 65.24,66.99 32.34,23.01 " style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform="  matrix(1 0 0 1 0 0) "/>
                              <path d="M 0 0 v 90 h 90 V 0 H 0 z M 56.032 70.504 L 41.054 50.477 L 22.516 70.504 h -4.765 L 38.925 47.63 L 17.884 19.496 h 16.217 L 47.895 37.94 l 17.072 -18.444 h 4.765 L 50.024 40.788 l 22.225 29.716 H 56.032 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class='w-full grid grid-cols-1 md:grid-cols-2 md:gap-6'>
                    <div v-for="(item, index) in restNewsItems" :key="index" class='flex flex-1 flex-col justify-between bg-white shadow overflow-hidden sm:rounded-lg py-8 px-6 mb-2'>
                      <div v-if='item.isPinned==1'>
                        <img class="float-right object-fill h-5" src="/img/pin.png" title="Pinned News Article">
                      </div>
                      <div>
                        <a href='javascript:void(0)' @click="navToNews(item.id, item.cat_url, item.titleSlug)" class="text-lg font-bold leading-5 text-blue-700 hover:text-blue-700 mt-4 mb-2 hover:underline">{{item.title}}</a>
                        <div class='text-xs text-grey-500'>Posted by {{item.by}} on {{new Date(item.date).toLocaleDateString('en-US', {month: 'long', year: 'numeric', day: 'numeric'})}}</div>
                      
                        <div v-if="isNewsSummary">
                          <div class="mt-4">                       
                            <div class='h-48 bg-gray-300 rounded animate-pulse mb-3'></div>
                            <div class='space-y-3'>
                              <div class='h-4 bg-gray-300 rounded animate-pulse'></div>
                              <div class='h-4 bg-gray-300 rounded w-5/6 animate-pulse'></div>
                              <div class='h-4 bg-gray-300 rounded w-3/4 animate-pulse'></div>
                            </div>
                          </div>
                          
                          <div class='mt-auto mb-0'>
                            <div class='h-4 bg-gray-300 rounded w-1/4 mt-4 animate-pulse'></div>
                          </div>
                        </div>
                        <div v-else class="news-body mt-8 leading-5">
                          <!-- Image carousel -->
                          <div v-if="item.images && item.images.length"  class="news-summary-carousel mb-4">
                            <!-- <div class="relative h-98 overflow-hidden rounded-lg"> -->
                            <div class="relative w-full h-96 overflow-hidden rounded-lg">

                              <transition name="slide" mode="out-in">
                                <img
                                  :key="item.id + '-' + (newsImageIndexes[item.id] || 0)"
                                  :src="'https://www.thebrokersite.com/images/news/articles/' +
                                      encodeURIComponent(
                                          item.images[newsImageIndexes[item.id] || 0].filename
                                      )"
                                  :alt="item.title"
                                  class="w-full h-full object-contain"
                                >
                              </transition>

                              <!-- Previous -->
                              <button v-if="item.images.length > 1" @click.stop="prevNewsSummaryImage(item)"
                                class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full w-9 h-9 flex items-center justify-center z-10">
                                &#10094;
                              </button>

                              <!-- Next -->
                              <button v-if="item.images.length > 1" @click.stop="nextNewsSummaryImage(item)"
                                class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full w-9 h-9 flex items-center justify-center z-10">
                                &#10095;
                              </button>

                              <!-- Indicators -->
                              <div v-if="item.images.length > 1"
                                class="absolute bottom-2 left-0 right-0 flex justify-center items-center gap-1 z-10">
                                <button
                                    v-for="(image, imageIndex) in item.images"
                                    :key="imageIndex"
                                    @click.stop="goToNewsSummaryImage(item, imageIndex)"
                                    class="rounded-full transition-all duration-200"
                                    :class="(newsImageIndexes[item.id] || 0) === imageIndex
                                        ? 'w-3 h-3 bg-blue-600'
                                        : 'w-2 h-2 bg-white bg-opacity-70'"
                                >
                                </button>
                              </div>
                            </div>
                          </div>

                          <!-- News body -->
                          <div
                              v-html="item.bodyHtml"
                              class="h-98 overflow-hidden text-ellipsis"
                          ></div>
                        </div>
                      </div>
                      <div class='mt-auto mb-0'>
                        <a v-if='item.doReadMore' href='javascript:void(0)' @click="navToNews(item.id, item.cat_url, item.titleSlug)" class='block mt-2 text-blue-700 hover:text-blue-700 hover:underline'>Read More</a>
                      </div>
                      <div class='grid grid-cols-2 w-full border-t mt-4 pt-4'>
                        <div>
                          <router-link :to="`/news/${getCategoryNameById(item.catId)}`" class='text-blue-700 hover:text-blue-700 truncate hover:underline'>More Related News</router-link>
                        </div>
                        <div v-if='settingsMap.enableNewsShareButtons == 1' class='grid grid-cols-3 gap-2 ml-auto mr-0 invisible md:visible'>
                          <div>
                            <a :href="'https://www.facebook.com/sharer/sharer.php?u='+encodeURIComponent('https://news.thebrokersite.com/?id='+item.id+'&share=facebook')" target="_blank" rel="noopener" title="Share on Facebook"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <path d="M 85.033 90 C 87.776 90 90 87.776 90 85.033 V 4.967 C 90 2.223 87.776 0 85.033 0 H 4.967 C 2.223 0 0 2.223 0 4.967 v 80.066 C 0 87.776 2.223 90 4.967 90 H 85.033 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(60,90,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 50.916 83.204 V 48.351 h 11.699 l 1.752 -13.583 h -13.45 v -8.672 c 0 -3.933 1.092 -6.612 6.731 -6.612 l 7.193 -0.003 V 7.332 c -1.244 -0.166 -5.513 -0.535 -10.481 -0.535 c -10.37 0 -17.47 6.33 -17.47 17.954 v 10.017 H 25.16 v 13.583 h 11.729 v 34.853 H 50.916 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>

                          <div>
                            <a :href="'https://www.linkedin.com/shareArticle?mini=true&url='+encodeURIComponent('https://news.thebrokersite.com/?id='+item.id+'&share=linkedin')" target="_blank" rel="noopener" title="Share on LinkedIn"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <path d="M 0 6.447 C 0 2.887 2.978 0 6.651 0 h 76.698 C 87.022 0 90 2.887 90 6.447 v 77.106 C 90 87.114 87.022 90 83.349 90 H 6.651 C 2.978 90 0 87.114 0 83.553 V 6.447 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,102,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 20.485 29.151 c 4.74 0 7.691 -3.121 7.691 -7.021 c -0.088 -3.988 -2.95 -7.022 -7.601 -7.022 c -4.65 0 -7.69 3.034 -7.69 7.022 c 0 3.9 2.95 7.021 7.512 7.021 H 20.485 L 20.485 29.151 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 27.282 75.339 v -40.64 H 13.688 v 40.64 H 27.282 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                              <path d="M 34.804 75.339 h 13.594 V 52.644 c 0 -1.215 0.088 -2.428 0.447 -3.296 c 0.983 -2.427 3.219 -4.94 6.975 -4.94 c 4.919 0 6.887 3.727 6.887 9.19 v 21.741 h 13.592 V 52.037 c 0 -12.483 -6.706 -18.291 -15.65 -18.291 c -7.333 0 -10.553 4.073 -12.342 6.847 h 0.091 v -5.894 H 34.804 C 34.982 38.513 34.804 75.339 34.804 75.339 L 34.804 75.339 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>

                          <div>
                            <a :href="'https://twitter.com/intent/tweet?text=' + encodeURIComponent(item.title) + '+' + encodeURIComponent('https://news.thebrokersite.com/?id='+item.id+'&share=x')" target="_blank" rel="noopener" title="Share on X (Twitter)"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                              <polygon points="24.89,23.01 57.79,66.99 65.24,66.99 32.34,23.01 " style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform="  matrix(1 0 0 1 0 0) "/>
                              <path d="M 0 0 v 90 h 90 V 0 H 0 z M 56.032 70.504 L 41.054 50.477 L 22.516 70.504 h -4.765 L 38.925 47.63 L 17.884 19.496 h 16.217 L 47.895 37.94 l 17.072 -18.444 h 4.765 L 50.024 40.788 l 22.225 29.716 H 56.032 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                            </g>
                            </svg></a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class='w-full' v-else>
                <div class='bg-white shadow overflow-hidden sm:rounded-lg py-8 px-6 mb-4'>
                  <h2 class="text-lg font-bold leading-5 text-blue-600 mt-4 mb-2">{{newsPostEnriched.title}}</h2>
                  <div v-if="newsPostEnriched.by"  class='text-xs text-grey-500'>Posted by {{newsPostEnriched.by}} on {{new Date(newsPostEnriched.date).toLocaleDateString('en-US', {month: 'long', year: 'numeric', day: 'numeric'})}}</div>
              
                  <div v-if="newsImages.length" class="relative w-full mb-8  mt-4 sm:mt-8">
                      <!-- Image area -->
                      <div class="w-full flex justify-center px-2 sm:px-12" style="height: 400px;">
                        <transition name="slide" mode="out-in">
                          <img
                              :key="currentImageIndex"
                              :src="'https://www.thebrokersite.com/images/news/articles/' + encodeURIComponent(newsImages[currentImageIndex].filename)"
                              :alt="newsPostEnriched.title"
                              class="news-carousel-image rounded-md"
                              @click="openImageModal"
                          >
                        </transition>
                      </div>

                      <!-- Previous -->
                      <button v-if="newsImages.length > 1" @click="prevImage" class="hidden sm:flex absolute left-0 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full w-10 h-10  items-center justify-center text-2xl z-10">
                          &#10094;
                      </button>

                      <!-- Next -->
                      <button v-if="newsImages.length > 1" @click="nextImage" class="hidden sm:flex absolute right-0 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full w-10 h-10  items-center justify-center text-2xl z-10">
                          &#10095;
                      </button>

                      <!-- Indicators -->
                      <div v-if="newsImages.length > 1" class="flex justify-center items-center gap-2 mt-3">
                          <button
                            v-for="(image, index) in newsImages"
                            :key="index"
                            @click="currentImageIndex = index"
                            class="rounded-full transition-all duration-200"
                            :class="index === currentImageIndex
                              ? 'w-3 h-3 bg-blue-600'
                              : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'">
                          </button>
                      </div>
                  </div>

                  <div v-html="newsPostEnriched.bodyHtml" class='news-body news-container mt-8 leading-5'></div>

                  <div class='grid grid-cols-2 w-full border-t mt-4 pt-4'>
                    <div>
                      <router-link :to="`/news/${getCategoryNameById(newsPostEnriched.catId)}`" class='text-blue-700 hover:text-blue-700 truncate hover:underline'>More Related News</router-link>
                    </div>
                    <div v-if='settingsMap.enableNewsShareButtons == 1' class='grid grid-cols-3 gap-2 ml-auto mr-0 invisible md:visible'>
                      <div>
                        <a :href="'https://www.facebook.com/sharer/sharer.php?u='+encodeURIComponent('https://news.thebrokersite.com/?id='+newsPostEnriched.id+'&share=facebook')" target="_blank" rel="noopener" title="Share on Facebook"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                        <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                          <path d="M 85.033 90 C 87.776 90 90 87.776 90 85.033 V 4.967 C 90 2.223 87.776 0 85.033 0 H 4.967 C 2.223 0 0 2.223 0 4.967 v 80.066 C 0 87.776 2.223 90 4.967 90 H 85.033 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(60,90,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                          <path d="M 50.916 83.204 V 48.351 h 11.699 l 1.752 -13.583 h -13.45 v -8.672 c 0 -3.933 1.092 -6.612 6.731 -6.612 l 7.193 -0.003 V 7.332 c -1.244 -0.166 -5.513 -0.535 -10.481 -0.535 c -10.37 0 -17.47 6.33 -17.47 17.954 v 10.017 H 25.16 v 13.583 h 11.729 v 34.853 H 50.916 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                        </g>
                        </svg></a>
                      </div>

                      <div>
                        <a :href="'https://www.linkedin.com/shareArticle?mini=true&url='+encodeURIComponent('https://news.thebrokersite.com/?id='+newsPostEnriched.id+'&share=linkedin')" target="_blank" rel="noopener" title="Share on LinkedIn"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                        <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                          <path d="M 0 6.447 C 0 2.887 2.978 0 6.651 0 h 76.698 C 87.022 0 90 2.887 90 6.447 v 77.106 C 90 87.114 87.022 90 83.349 90 H 6.651 C 2.978 90 0 87.114 0 83.553 V 6.447 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,102,153); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                          <path d="M 20.485 29.151 c 4.74 0 7.691 -3.121 7.691 -7.021 c -0.088 -3.988 -2.95 -7.022 -7.601 -7.022 c -4.65 0 -7.69 3.034 -7.69 7.022 c 0 3.9 2.95 7.021 7.512 7.021 H 20.485 L 20.485 29.151 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                          <path d="M 27.282 75.339 v -40.64 H 13.688 v 40.64 H 27.282 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                          <path d="M 34.804 75.339 h 13.594 V 52.644 c 0 -1.215 0.088 -2.428 0.447 -3.296 c 0.983 -2.427 3.219 -4.94 6.975 -4.94 c 4.919 0 6.887 3.727 6.887 9.19 v 21.741 h 13.592 V 52.037 c 0 -12.483 -6.706 -18.291 -15.65 -18.291 c -7.333 0 -10.553 4.073 -12.342 6.847 h 0.091 v -5.894 H 34.804 C 34.982 38.513 34.804 75.339 34.804 75.339 L 34.804 75.339 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                        </g>
                        </svg></a>
                      </div>

                        <div>
                          <a :href="'https://twitter.com/intent/tweet?text=' + encodeURIComponent(newsPostEnriched.title) + '+' + encodeURIComponent('https://news.thebrokersite.com/?id='+newsPostEnriched.id+'&share=x')" target="_blank" rel="noopener" title="Share on X (Twitter)"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="30" height="30" viewBox="0 0 256 256" xml:space="preserve">
                          <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
                            <polygon points="24.89,23.01 57.79,66.99 65.24,66.99 32.34,23.01 " style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform="  matrix(1 0 0 1 0 0) "/>
                            <path d="M 0 0 v 90 h 90 V 0 H 0 z M 56.032 70.504 L 41.054 50.477 L 22.516 70.504 h -4.765 L 38.925 47.63 L 17.884 19.496 h 16.217 L 47.895 37.94 l 17.072 -18.444 h 4.765 L 50.024 40.788 l 22.225 29.716 H 56.032 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
                          </g>
                          </svg></a>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
              <Pagination v-if='!isDetailPage' :itemList="newsEnriched" :payload="payload" :pageSize="pageSize"></Pagination>
            </div>

          </div>
          <div class="col-span-12 invisible lg:visible lg:col-span-3 overflow-hidden px-4 mb-8">
            <h2 class="text-lg font-bold leading-5 text-red-600 mt-4 mb-4">Latest Posts</h2>

            <ul class='text-sm'>
              <!-- Skeleton loader when content is loading -->
              <template v-if='latestPosts.length <= 0'>
                <li v-for="index in 5" :key="index" class='space-y-2 mb-2'>
                  <div class='h-4 bg-gray-300 rounded animate-pulse'></div>
                  <div class='h-4 bg-gray-300 rounded animate-pulse w-4/5'></div>
                </li>
              </template>
              
              <!-- Latest Post -->
              <template v-else>
                <li v-for="(item,index) in latestPosts" :key="index">
                  <a href='javascript:void(0)' class='text-red-600 block truncate hover:underline' @click="navToNews(item.id, item.cat_url, item.titleSlug)" :title='item.title'>{{item.title}}</a>
                </li>
              </template>
            </ul>

            <h2 class="text-lg font-bold leading-5 text-red-600 mt-12 mb-4">Categories</h2>
            <ul class='text-sm'>
              <li>
                <div class='clearfix'>
                  <router-link :to="{name: 'news'}" class='float-left text-red-600  truncate hover:underline'>All</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS()' title='Subscribe to the full news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(9)}`" class='float-left text-red-600 truncate hover:underline'>Blog</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(9)' title='Subscribe to the Blog news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(12)}`" class='float-left text-red-600 truncate hover:underline'>Companies for Sale</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(12)' title='Subscribe to the companies for sale news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(2)}`" class='float-left text-red-600 truncate hover:underline'>European Broker Meeting</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(2)' title='Subscribe to the European Broker Meeting news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(1)}`" class='float-left text-red-600 truncate hover:underline'>Fraud Warnings</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(1)' title='Subscribe to the Fraud Warnings news feed'></a>
                </div>
                <!-- <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(11)}`" class='float-left text-red-600 truncate hover:underline'>Industry Jobs</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(1)' title='Subscribe to the Industry Jobs news feed'></a>
                </div> -->
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(3)}`" class='float-left text-red-600 truncate hover:underline'>Members</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(3)' title='Subscribe to the Members news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(10)}`" class='float-left text-red-600 truncate hover:underline'>Stolen Equipment</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(10)' title='Subscribe to the Stolen Equipment news feed'></a>
                </div>
                <div class='clearfix'>
                  <router-link :to="`/news/${getCategoryNameById(4)}`" class='float-left text-red-600 truncate hover:underline'>The Broker Site</router-link>
                  <a class='float-left ml-2 rssSmall cursor-pointer' target="_blank" :href='toRSS(4)' title='Subscribe to the The Broker Site news feed'></a>
                </div>
              </li>
            </ul>

            <div class='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-5'>
              <div class='sm:col-span-3'><input v-model='payload.query' class="form-input w-full" placeholder="Search" @keyup.enter='search'></div>
              <div class='sm:col-span-2'><button @click='search' type="button" class="btn btn-primary">Search</button></div>
              <!-- Hidden fake email field to trick browser -->
              <input type="fakeEmail" name="fakeEmail" style="position: absolute; left: -9999px; opacity: 0;" tabindex="-1" autocomplete="off" value="">

            </div>

            <span class="hidden lg:block mt-16"><a href='http://eepurl.com/bG65cr' target='_blank' rel='noopener' class="border border-transparent font-medium rounded-md text-white bg-red-700 hover:bg-red-700 py-4 text-sm px-7 m-auto">Subscribe to our Newsletter</a></span>

          </div>
        </div>


        <!-- Image modal -->
        <div v-if="showImageModal" class="image-modal" @click.self="closeImageModal">
          <!-- Close -->
          <button @click="closeImageModal" class="image-modal-close">
              &times;
          </button>

          <!-- Previous -->
          <button v-if="newsImages.length > 1" @click="prevImage" class="image-modal-prev">&#10094;</button>

          <!-- Image -->
          <img
              :src="'https://www.thebrokersite.com/images/news/articles/' + encodeURIComponent(newsImages[currentImageIndex].filename)"
              :alt="newsPostEnriched.title"
              class="image-modal-image">

          <!-- Next -->
          <button v-if="newsImages.length > 1" @click="nextImage" class="image-modal-next" > &#10095;</button>
        </div>
      </div>
    </template>
  </base-layout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue'
import ListMixin from '@/components/mixins/List'
import { mapActions, mapState, mapGetters } from 'vuex';
import { useSchemaOrg } from '../composables/useSchema.org.js';
import store from '../store'

export default {
  mixins: [ListMixin],
  name: 'News',
  components: {
    BaseLayout
  },
  data: () => ({
    // pageSize: 9,
    latestPosts: [],
    payload: {
      query: null, 
      catId: "",
      page: 1,
    },
    fetchActionName: "getNews",
    numResultsName: "nrNewsItems",
    categoryTitle: '',
    categoryDescription: '',
    categories: [
      { catId: 9, catNameShort: "blog", catName: "Blog" },
      { catId: 12, catNameShort: "companies-for-sale", catName: "Companies for Sale" },
      { catId: 2, catNameShort: "european-broker-meeting", catName: "European Broker Meeting" },
      { catId: 1, catNameShort: "fraud-warnings", catName: "Fraud Warnings" },
      // { catId: 11, catNameShort: "IndustryJobs", catName: "Industry Jobs" },
      { catId: 3, catNameShort: "members", catName: "Members" },
      { catId: 10, catNameShort: "stolen-equipment", catName: "Stolen Equipment" },
      { catId: 4, catNameShort: "the-broker-site", catName: "The Broker Site" },
    ],
    newsCategories: [],
    shouldUpdateNews: true,
    retrieveCount: 0,
    newsImages: [],
    currentImageIndex: 0,
    showImageModal: false,
    imageTransition: 'next',
    newsImageIndexes: {}
  }),
	watch: {
		'$route.params.categoryName': 'fetchNewsByCategory',
    newsPostEnriched: {
      handler(newVal) {
        if (newVal && this.$route.query.id) {
          this.setCategoryDescription(); 
          this.addArticleSchema(); 
          this.fetchNewsImages();
        }
      },
      deep: true,
      immediate: false
    },
    newsEnriched: {
      handler(newVal) {
        if (newVal?.length) {
          this.addNewsListSchema(); 
          if(newVal[0].body) {
            this.loadSecondPart();
          }
        }
      },
      deep: true,
      immediate: false
    },
    newsCategories: {
      handler(newVal) {
        if (newVal) {
          this.addNewsListSchema();
        }
      },
      deep: true,
      immediate: false
    },
  },
  computed: {
    ...mapState([ 'nrNewsItems', 'settingsMap', 'isFetchingNews', 'newsPayload', 'hasLoadedSecondPart', 'isNewsSummary', 'user']),
    ...mapGetters(['newsEnriched', 'newsPostEnriched']),
    isDetailPage(){
      return !!this.$route.query.id;
    },
    firstNewsItem() {
      const item = Object.values(this.newsEnriched)[0]
      if (item) {
        item.bodyHtml = this.processImageUrls(item.bodyHtml)
      }
      return item
    },
    restNewsItems() {
      return Object.values(this.newsEnriched).slice(1).map(item => {
        item.bodyHtml = this.processImageUrls(item.bodyHtml)
        return item
      })
    },
  },
  created(){
    store.dispatch('resetNewsUnread');
    if(this.user) {
      this.resetUnreadNewsCount();
    }
    this.getLatestPosts().then(data => {
      this.latestPosts = data.payload
    });
	  this.fetchNewsByCategory();
    this.getNewsCategories();
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
      window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    ...mapActions(["getNews", 'getLatestPosts', 'getNewsPost', 'getNewsSummary', 'resetUnreadNewsCount']),
    navToNews(id, catURL = "", titleSlug = ""){
      // this.$router.push({ name: 'newsDetail', params: { id: "" + id } })
      const url = `/news/${catURL}/${titleSlug}?id=${id}`;
      this.$router.push(url);
    },
    async loadDetail(id){
      try {
          await this.getNewsPost(id);
          // News post loaded successfully
      } catch (error) {
          this.$router.push('/news');
      }
      this.updateMetaTags();
    },
    async search() {
      const isInNewsDetail = this.$route.name === 'newsDetail';
      
      // First perform the search
      this.payload.catId = "";
      await this.getNews(this.payload);

      if (isInNewsDetail) {
        await this.$router.replace({ 
          path: '/news',
          query: { 
            query: this.payload.query
          }
        });
        
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    toRSS(catId){
      const loc = window.location;
      const isDevMode = loc.port === "8080";
      const p = this.$route.fullPath;
      
      let q = ""

      const category = this.categories.find(c => c.catId === catId);
      if(catId){
        q = `/${category?.catNameShort}`;
      }
      const url = (!isDevMode ? '/api/news/rss' : `${loc.protocol}//${loc.hostname}:3000/api/news/rss`) + q;
      return url;
    },
    getCategoryNameById(catId) {
      const category = this.categories.find(cat => cat.catId === catId)
      return category ? category.catNameShort : ''
    },
    getCategoryNameByIdLong(catId) {
      const category = this.categories.find(cat => cat.catId === catId)
      return category ? category.catName : ''
    },
    getCategoryIdByName(catNameShort) {
      const category = this.categories.find(cat => cat.catNameShort === catNameShort)
      return category ? category : null
    },
    fetchNewsByCategory() {
      this.setCategoryDescription();
      this.updateOrCreateLinkTag('canonical', window.location.href);
      const categoryName = this.$route.params.categoryName;
      const category = this.getCategoryIdByName(categoryName);
      
        
      const checkIfNumeric = this.isNumeric(categoryName)
      if (!checkIfNumeric) {
        if (!categoryName) {
            this.categoryName = this.getCategoryNameById(this.payload.catId)            
            this.categoryTitle = ''
            this.payload.catId = null
            this.newscategoryid = null;
            return;
        }       

        this.newscategoryid = category.catId;
        this.categoryTitle = category ? category.catName : ''
        this.payload.catId = category ? category.catId : null
      } else {
        // load the news detail
        this.$router.push({ name: 'newsDetail', params: { id: "" + categoryName } })
      }
    },
    isNumeric(value) {
      return !isNaN(value) && !isNaN(parseFloat(value))
    },
    processImageUrls(html) {
      if (!html) return html
      
      // Create a DOMParser to properly parse and manipulate the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Process YouTube iframes to replace with thumbnails
      doc.querySelectorAll('iframe').forEach(iframe => {
        const src = iframe.getAttribute('src');
        if (src && (src.includes('youtube.com/') || src.includes('youtu.be'))) {
          // Extract video ID from different YouTube URL formats
          let videoId = null;
          const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
          const match = src.match(youtubeRegex);
          
          if (match && match[1]) {
            videoId = match[1];
            
            // Create thumbnail image
            const img = doc.createElement('img');
            img.setAttribute('src', `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
            img.setAttribute('alt', 'YouTube Video Thumbnail');
            img.setAttribute('class', 'youtube-thumbnail');
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.margin = '8px 0';
            img.style.borderRadius = '4px';
            
            // Replace iframe with thumbnail image
            iframe.parentNode.replaceChild(img, iframe);
          }
        }
      });
      
      // Find all img elements and process their src attributes
      doc.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src');
        if (src) {
          const newSrc = src.startsWith('http') || src.startsWith('data:') 
            ? src 
            : 'https://www.thebrokersite.com/' + src;
          img.setAttribute('src', newSrc);
        }
      });
      
      // Return the processed HTML
      return doc.body.innerHTML;
    },
    updateMetaTags() {
      if (this.newsPostEnriched) {  
        // Truncate title if needed (60 chars max)
        const title = (this.newsPostEnriched.title || '').substring(0, 55);

        const metaTitle = this.newsPostEnriched.metaTitle || '';
        const metaDescription = this.newsPostEnriched.metaDescription || '';
        const metaKeywords = this.newsPostEnriched.metaKeywords || '';





        const rawKeywords  = metaKeywords || metaTitle || title;
        const finalMetaTitle = metaTitle || title || '';
        let finalMetaDescription = '';

        const finalMetaKeywords = rawKeywords.split(' ')
          .map(keyword => keyword.trim())
          .filter(keyword => keyword.length > 3)
          .join(', ');
        
        if(!metaDescription) {
          const cleanDescription = this.createCleanDescription(this.newsPostEnriched.body);
          finalMetaDescription = cleanDescription || '';
        } else {
          finalMetaDescription = metaDescription;
        }

        // Set the document title
        document.title = finalMetaTitle;

        // Update or create meta description
        this.updateOrCreateMetaTag('name', 'description', finalMetaDescription);
        // Update or create meta keywords
        this.updateOrCreateMetaTag('name', 'keywords', finalMetaKeywords);

        // Update or create OG meta tags
        this.updateOrCreateMetaTag('property', 'og:title', finalMetaTitle);
        this.updateOrCreateMetaTag('property', 'og:description', finalMetaDescription);
        this.updateOrCreateMetaTag('property', 'og:url', window.location.href);
        this.updateOrCreateMetaTag('property', 'og:type', 'article');
        
        // Update or create canonical link
        this.updateOrCreateLinkTag('canonical', window.location.href);
      }
    },

    updateOrCreateMetaTag(attrType, attrValue, content) {
      let selector = attrType === 'name' 
        ? `meta[name="${attrValue}"]` 
        : `meta[property="${attrValue}"]`;
      
      let metaTag = document.querySelector(selector);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        if (attrType === 'name') {
          metaTag.setAttribute('name', attrValue);
        } else {
          metaTag.setAttribute('property', attrValue);
        }
        document.head.appendChild(metaTag);
      }
      
      metaTag.setAttribute('content', content);
    },

    updateOrCreateLinkTag(rel, href) {
      let linkTag = document.querySelector(`link[rel="${rel}"]`);
      
      if (!linkTag) {
        linkTag = document.createElement('link');
        linkTag.setAttribute('rel', rel);
        document.head.appendChild(linkTag);
      }
      
      linkTag.setAttribute('href', href);
    },

    createCleanDescription(htmlContent) {
      if (!htmlContent) return '';
      
      // Simple regex to remove HTML tags
      let text = htmlContent.replace(/<[^>]*>/g, ' ');
      
      // Remove base64 images (simplified approach)
      text = text.replace(/data:image[^'"]+/g, ' ');
      
      // Clean up whitespace
      text = text.replace(/\s+/g, ' ').trim();
      
      // Truncate
      if (text.length > 150) {
        text = text.substring(0, 147) + '...';
      }
      
      return text;
    },
    async getNewsCategories() {
      const res = await this.$http.get("/api/news/categories");
      this.newsCategories = res.data;      
      this.setCategoryDescription();
    },
    setCategoryDescription() {
      const categoryName = this.$route.params.categoryName;
      if(categoryName && this.newsCategories.length > 0){
        const description = this.newsCategories.find(cat => cat.catUrl === categoryName);
        this.categoryDescription = description.catDescription ?? '';
      } else {
        const categoryId = this.newsPostEnriched.catId
        if(categoryId && this.newsCategories.length > 0) {
          const description = this.newsCategories.find(cat => cat.id === categoryId);
          this.categoryDescription = description.catDescription ?? '';
        } else{
          this.categoryDescription = '';
        }
      }
    },
    addArticleSchema() {
      if(this.$route.query.id) {      
        const articleSchema = {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": this.newsPostEnriched.title,
          "description": this.newsPostEnriched.metaDescription || this.newsPostEnriched.title,
          "articleSection": this.newsPostEnriched.catName,
          "datePublished": this.newsPostEnriched.date,
          "keywords": this.newsPostEnriched.metaKeywords || '',
          "author": {
            "@type": "Person",
            "name": this.newsPostEnriched.by,
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": typeof window !== 'undefined' ? window.location.href : '' 
          }
        };
        useSchemaOrg(articleSchema);
      } 
    },
    addNewsListSchema() {
      if (this.newsEnriched && this.newsEnriched.length > 0) {
        const listSchema = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "about": this.newsCategories.map(cat => ({
            "@type": "Thing",
            "name": cat.catName,
            "description": cat.catDescription && cat.catDescription.trim() !== "" 
              ? cat.catDescription 
              : cat.catName
          })),
          "itemListElement": this.newsEnriched.map((news, index) => ({
            "position": index + 1,
            "@type": "ListItem",
            "url": `https://www.thebrokersite.com/news/${news.catURL}/${news.titleSlug}?id=${news.id}`,
            "name": news.title,
            "description": news.metaDescription || news.title,
            "datePublished": news.date,
            "category": news.catName || '' 
          }))
        };
        useSchemaOrg(listSchema);
      }
    },
    async loadSecondPart() {
      if(this.hasLoadedSecondPart) {
        return;
      }
      const payload = {
        isSecondPart: true
      }
      this.getNews(payload);
    },
    async fetchNewsImages() {
        const newsId = this.$route.query.id;
        this.newsImages = [];

        if (newsId) {
            const resp = await this.$http.get(`/api/news/images/${newsId}`);

            if (resp.data.status === 200) {
                this.newsImages = resp.data.data || [];
                this.currentImageIndex = 0;
                this.preloadNewsImages();
            }
        }
    },
    preloadNewsImages() {
      this.newsImages.forEach(image => {
          const img = new Image();

          img.src =
              'https://www.thebrokersite.com/images/news/articles/' +
              encodeURIComponent(image.filename);
      });
    },
    nextImage() {
      this.imageTransition = 'next';
      this.currentImageIndex =
        this.currentImageIndex === this.newsImages.length - 1
          ? 0
          : this.currentImageIndex + 1;
    },
    prevImage() {
      this.imageTransition = 'prev';

      this.currentImageIndex =
        this.currentImageIndex === 0
            ? this.newsImages.length - 1
            : this.currentImageIndex - 1;
    },
    openImageModal() {
        this.showImageModal = true;
    },
    closeImageModal() {
        this.showImageModal = false;
    },
    handleKeydown(event) {
      if (!this.showImageModal) {
          return;
      }

      if (event.key === 'ArrowRight') {
          this.nextImage();
      }

      if (event.key === 'ArrowLeft') {
          this.prevImage();
      }

      if (event.key === 'Escape') {
          this.closeImageModal();
      }
    },
    nextNewsSummaryImage(item) {
      const currentIndex = this.newsImageIndexes[item.id] || 0;

      this.$set(
          this.newsImageIndexes,
          item.id,
          currentIndex === item.images.length - 1
              ? 0
              : currentIndex + 1
      );
    },
    prevNewsSummaryImage(item) {
      const currentIndex = this.newsImageIndexes[item.id] || 0;

      this.$set(
        this.newsImageIndexes,
        item.id,
        currentIndex === 0
          ? item.images.length - 1
          : currentIndex - 1
      );
    },
    goToNewsSummaryImage(item, imageIndex) {
      this.$set(
        this.newsImageIndexes,
        item.id,
        imageIndex
      );
    },
    
  },
}
</script>
<style>
  .news-body img{
    margin-top: 5px;
  }
  /* em preceded by a quote */
  .news-body img + br + em {
    margin-top: -4rem;
    padding-top: 3.5rem;
  }
  /* end-quote */
  .news-body em + br + img{
    display: none;
  }
  .rssSmall {
    display: inline-block;
    width: 17px;
    height: 17px;
    background-image: url(https://www.thebrokersite.com/images/main_images.png);
    background-position: -358px -354px;
    cursor: auto;
  }

  .news-container h2,
  .news-container h3,
  .news-container h4,
  .news-container h5,
  .news-container h6 {
    font-size: unset;
    font-weight: unset;
    margin: unset;
  }

  .news-container h2 {
    font-size: clamp(1.5rem, 4vw, 1.8rem);
    font-weight: 600;
    margin: 1.5rem 0;
    line-height: 1.3;
  }

  .news-container h3 {
    font-size: clamp(1.3rem, 3.5vw, 1.5rem);
    font-weight: 600;
    margin: 1.2rem 0;
    line-height: 1.3;
  }

  .news-container h4 {
    font-size: clamp(1.1rem, 3vw, 1.3rem);
    font-weight: 600;
    margin: 1rem 0;
    line-height: 1.4;
  }

  .news-container h5 {
    font-size: clamp(1rem, 2.5vw, 1.1rem);
    font-weight: 600;
    margin: 0.8rem 0;
    line-height: 1.4;
  }

  .news-container h6 {
    font-size: clamp(0.9rem, 2vw, 1rem);
    font-weight: 600;
    margin: 0.6rem 0;
    line-height: 1.4;
  }




  .news-carousel-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    border-radius: 150px;
    cursor: zoom-in;
  }

  /* Carousel animation */
  .slide-enter-active,
  .slide-leave-active {
      transition: all 0.25s ease;
  }

  .slide-enter {
      opacity: 0;
      transform: translateX(30px);
  }

  .slide-leave-to {
      opacity: 0;
      transform: translateX(-30px);
  }



  /* Fullscreen modal */
  .image-modal {
      position: fixed;
      z-index: 99999;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      background: rgba(0, 0, 0, 0.9);

      display: flex;
      align-items: center;
      justify-content: center;

      padding: 40px;
  }

  .image-modal-image {
      max-width: 95vw;
      max-height: 90vh;

      width: auto;
      height: auto;

      object-fit: contain;

      border-radius: 8px;

      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  }


  /* Modal buttons */
  .image-modal-close,
  .image-modal-prev,
  .image-modal-next {
      position: absolute;

      background: rgba(255, 255, 255, 0.15);
      color: white;

      border: none;
      border-radius: 50%;

      width: 45px;
      height: 45px;

      display: flex;
      align-items: center;
      justify-content: center;

      cursor: pointer;

      transition: background 0.2s;
  }

  .image-modal-close:hover,
  .image-modal-prev:hover,
  .image-modal-next:hover {
      background: rgba(255, 255, 255, 0.3);
  }

  .image-modal-close {
      top: 20px;
      right: 25px;

      font-size: 32px;
  }

  .image-modal-prev {
      left: 25px;
      top: 50%;
      transform: translateY(-50%);

      font-size: 24px;
  }

  .image-modal-next {
      right: 25px;
      top: 50%;
      transform: translateY(-50%);

      font-size: 24px;
  }
</style>


